<script setup>
import { getCurrentInstance, inject, provide, ref } from "vue";
import Sidebar from "./Sidebar.vue";
import { useI18n } from "vue-i18n";
import { UserIcon, ShareIcon, HomeIcon } from "@heroicons/vue/24/outline";
import { AmazonIcon, MagicIcon, PlugLineIcon, WordPressIcon } from "@Icons/index";
import AppLink from "@Components/AppLink.vue";
import { useRoute } from "vue-router";
import Badge from "@Components/Badge.vue";
import TemplatesModal from "@Components/Templates/TemplatesModal.vue";
import ToolsMenu from "@Layouts/Partials/ToolsMenu.vue";
import {
    BrainCircuitIcon,
    CalendarClockIcon,
    GlobeIcon,
    ImageIcon,
    KeyIcon,
    LanguagesIcon,
    LayoutTemplateIcon,
    LinkIcon,
    Share2Icon,
    StoreIcon,
    TelescopeIcon,
    UsersIcon,
    VideoIcon,
    WandSparklesIcon,
} from "lucide-vue-next";
import { PrimaryButton } from "@Components/Button";

const { t: __ } = useI18n();
const route = useRoute();
const globalRefs = inject("globalRefs");

const templatesModal = ref();
const toolsMenu = ref();

let navigation = [
    {
        title: __("SEO"),
        items: [
            { icon: HomeIcon, name: __("Dashboard"), href: { name: "dashboard" }, match: "^dashboard" },
            {
                icon: GlobeIcon,
                name: __("Mes sites"),
                href: getCurrentInstance().appContext.config.globalProperties.$app.saas_url,
                target: "_blank",
            },
            { icon: UsersIcon, name: __("Rédacteurs IA"), href: { name: "personas.index" }, match: "^personas\\..*" },
            {
                icon: MagicIcon,
                name: __("Assistant IA"),
                href:
                    getCurrentInstance().appContext.config.globalProperties.$app.saas_url +
                    "/documents/create/" +
                    getCurrentInstance().appContext.config.globalProperties.$app.domain.id,
                target: "_blank",
            },
            { icon: BrainCircuitIcon, name: __("Autopilot"), href: { name: "projects.index" }, match: "^projects|publications\\..*" },
            {
                icon: LinkIcon,
                name: __("Link Building"),
                href: getCurrentInstance().appContext.config.globalProperties.$app.saas_url + "/netlinking",
                target: "_blank",
            },
            {
                icon: TelescopeIcon,
                name: __("Domaine expirés"),
                href: getCurrentInstance().appContext.config.globalProperties.$app.saas_url + "/expired-domains",
                target: "_blank",
            },
            {
                icon: StoreIcon,
                name: __("Marketplace"),
                coming_soon: true,
            },
        ],
    },
    {
        title: __("Média IA"),
        items: [
            { icon: ImageIcon, name: __("Image IA"), href: { name: "image-generator" }, match: "^image-generator" },
            { icon: VideoIcon, name: __("Vidéo IA"), coming_soon: true },
            { icon: Share2Icon, name: __("Social IA"), coming_soon: true },
        ],
    },
    {
        title: __("Réglages"),
        items: [
            { icon: KeyIcon, name: __("Clés API"), href: { name: "settings" }, match: "^settings.api" },
            { icon: MagicIcon, name: __("Custom IA"), href: { name: "settings.global-systems" }, match: "^settings.global-systems" },
            { icon: AmazonIcon, name: __("Amazon"), href: { name: "settings.amazon" }, match: "^settings.amazon" },
            { icon: ShareIcon, name: __("Related Content"), href: { name: "linking.automatic.posts" }, match: "^linking" },
            {
                icon: LayoutTemplateIcon,
                name: __("Templates"),
                onClick: () => {
                    templatesModal.value.show();
                },
            },
        ],
    },
];

globalRefs.toolsMenu = toolsMenu;

const current = (match) => {
    if (match !== undefined) {
        return route.name.match(new RegExp(match)) !== null;
    }

    return false;
};
</script>

<template>
    <Sidebar :back="{ to: getCurrentInstance().appContext.config.globalProperties.$admin(), label: __('Retour sur l\'admin') }">
        <!-- <div class="flex-none px-6"> -->
        <ToolsMenu ref="toolsMenu" />
        <!-- </div> -->

        <div class="flex-grow overflow-y-auto flex flex-col mt-3">
            <div class="flex flex-col flex-1 space-y-6">
                <div v-for="(group, index) in navigation" :key="index">
                    <div v-if="group.title" class="px-6 mb-3 leading-6 text-gray-900 dark:text-white">{{ group.title }}</div>
                    <div v-for="(item, index2) in group.items" :key="index2">
                        <component
                            :is="item.href ? AppLink : 'div'"
                            :to="item.href"
                            @click="item.onClick ? item.onClick() : undefined"
                            :target="item.target"
                            :current="current(item.match)"
                            :class="[
                                current(item.match)
                                    ? 'border-gray-950 bg-gray-50 dark:bg-gray-800 dark:border-gray-600'
                                    : [
                                          item.href || item.onClick
                                              ? 'hover:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 dark:hover:border-gray-700'
                                              : '',
                                          'border-transparent',
                                      ],
                                'text-gray-900 dark:text-gray-200 group flex items-center gap-x-1.5 px-6 py-2 text-sm leading-6 border-l-2',
                            ]">
                            <div class="flex items-center gap-x-3">
                                <component
                                    :is="item.icon"
                                    :class="[current(item.match) ? 'text-gray-950 dark:text-white' : '', 'w-6 h-6 shrink-0 text-gray-800 dark:text-gray-200']"
                                    aria-hidden="true" />
                                {{ item.name }}
                            </div>
                            <Badge v-if="item.coming_soon" color="black" class="ml-auto shrink-0">{{ __("Soon") }} </Badge>
                            <Badge v-if="item.new" color="black" class="ml-auto shrink-0">{{ __("Nouveau") }}</Badge>
                        </component>
                    </div>
                </div>
            </div>
        </div>
        <TemplatesModal ref="templatesModal" />
    </Sidebar>
</template>
